import { useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useLoader } from "../context/LoaderContext";
import { useToast } from "./useToast";

import { generateDigilockerUrl } from "../api/services/kycDigilockerGenerateUrl.service";
import { kycDigilockerUpdate } from "../api/services/kycDigilockerUpdate.service";

import useAppStore from "../store/useAppStore";

import { DOCUMENT_STATUS } from "../store/slices/kycDocumentsSlice";
import { Native } from "../platform/android/nativeBridge";

const DIGILOCKER_SUCCESS_EVENT = "DIGILOCKER_SUCCESS_EVENT";
const DIGILOCKER_ERROR_EVENT = "DIGILOCKER_ERROR_EVENT";
const DIGILOCKER_FLOW_STARTED_KEY = "digilockerFlowStarted";
const PENDING_REDIRECT_URL_KEY = "pendingNetBankingCallbackUrl";

const clearFlowStorage = () => {
  localStorage.removeItem(DIGILOCKER_FLOW_STARTED_KEY);
  localStorage.removeItem(PENDING_REDIRECT_URL_KEY);
};

// Check if current window is a DigiLocker desktop popup
const searchParams = new URLSearchParams(window.location.search);
if (searchParams.get("isPopup") === "true") {
  const success = searchParams.get("success");
  localStorage.setItem(
    success === "true" ? DIGILOCKER_SUCCESS_EVENT : DIGILOCKER_ERROR_EVENT,
    Date.now().toString()
  );
  window.close();
}

const STOP_LOADER_DELAY_MS = 2000;

export const useDigilocker = (isReupload = false) => {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const { startLoader, stopLoader } = useLoader();

  const updateKycDocumentStatus = useAppStore((state) => state.updateKycDocumentStatus);
  const updateReuploadDocumentStatus = useAppStore((state) => state.updateReuploadDocumentStatus);
  const updateStatus = isReupload ? updateReuploadDocumentStatus : updateKycDocumentStatus;

  const digilockerUniqueId = useAppStore((state) => state.digilockerUniqueId);
  const digilockerTransactionId = useAppStore((state) => state.digilockerTransactionId);

  const setDigilockerUniqueId = useAppStore((state) => state.setDigilockerUniqueId);
  const setDigilockerTransactionId = useAppStore((state) => state.setDigilockerTransactionId);
  const isDummyUser = useAppStore((state) => state.isDummyUser);

  const handleDigilockerUpdate = useCallback(async () => {
    if (!digilockerUniqueId || !digilockerTransactionId) {
      return;
    }

    startLoader("Verifying your information securely...");

    try {
      const payload = {
        properties: {
          uniqueId: digilockerUniqueId,
          transactionId: digilockerTransactionId,
        },
      };

      await kycDigilockerUpdate(payload);

      setDigilockerUniqueId(null);
      setDigilockerTransactionId(null);
    } catch (error) {
      showToast(error?.message || "Failed to update DigiLocker status", {
        type: "error",
      });
    } finally {
      stopLoader();
    }
  }, [
    digilockerUniqueId,
    digilockerTransactionId,
    setDigilockerUniqueId,
    setDigilockerTransactionId,
    startLoader,
    stopLoader,
    showToast,
  ]);

  const handleDigilockerReturn = useCallback(
    (success) => {
      if (success) {
        updateStatus("address", DOCUMENT_STATUS.UPLOADED, null);
        if (!isDummyUser) {
          handleDigilockerUpdate();
        }
      } else {
        setDigilockerUniqueId(null);
        setDigilockerTransactionId(null);
        showToast("DigiLocker process failed or incomplete", { type: "error" });
      }
    },
    [
      updateStatus,
      handleDigilockerUpdate,
      showToast,
      setDigilockerUniqueId,
      setDigilockerTransactionId,
      isDummyUser,
    ]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get("success");
    const error = params.get("error");
    const flowStarted = localStorage.getItem(DIGILOCKER_FLOW_STARTED_KEY);

    // Mobile redirect path: URL has ?success or ?error — handle directly
    if (flowStarted && success) {
      clearFlowStorage();
      handleDigilockerReturn(success === "true");
    } else if (flowStarted && error) {
      clearFlowStorage();
      handleDigilockerReturn(false);
    }

    // Clean browser URL bar and MemoryRouter location so page refresh cleanly loads "/"
    if (location.search || window.location.search) {
      window.history.replaceState(null, "", "/");
      clearFlowStorage();
      navigate(location.pathname, {
        replace: true,
      });
    }

    // Desktop popup path: only listen for storage events when not handling mobile redirect params
    if (!success && !error) {
      const handleStorageEvent = (e) => {
        if (e.key === DIGILOCKER_SUCCESS_EVENT || e.key === DIGILOCKER_ERROR_EVENT) {
          const isSuccess = e.key === DIGILOCKER_SUCCESS_EVENT;
          localStorage.removeItem(e.key);
          clearFlowStorage();
          handleDigilockerReturn(isSuccess);
        }
      };
      window.addEventListener("storage", handleStorageEvent);
      return () => window.removeEventListener("storage", handleStorageEvent);
    }
  }, [navigate, location.search, location.pathname, handleDigilockerReturn]);

  const handleDigiLockerClick = useCallback(async () => {
    try {
      startLoader("Initializing DigiLocker...");

      const isDesktop = !Native.isApp;

      const payload = {
        redirectUrl: `${window.location.origin}${location.pathname}${isDesktop ? "?isPopup=true" : ""}`,
      };

      localStorage.setItem(DIGILOCKER_FLOW_STARTED_KEY, "started");

      const response = await generateDigilockerUrl(payload);

      if (response?.url) {
        if (response?.uniqueId) {
          setDigilockerUniqueId(response.uniqueId);
        }

        if (response?.transactionId) {
          setDigilockerTransactionId(response.transactionId);
        }

        if (isDesktop) {
          window.open(response.url, "Digilocker");
        } else {
          window.location.href = response.url;
        }

        return;
      }

      clearFlowStorage();
      showToast("Failed to generate DigiLocker URL", {
        type: "error",
      });
    } catch (error) {
      clearFlowStorage();
      showToast(error?.message || "Something went wrong", {
        type: "error",
      });
    } finally {
      setTimeout(() => {
        stopLoader();
      }, STOP_LOADER_DELAY_MS);
    }
  }, [
    startLoader,
    stopLoader,
    showToast,
    setDigilockerUniqueId,
    setDigilockerTransactionId,
    location.pathname,
  ]);

  return {
    handleDigiLockerClick,
  };
};
